// components/IndicesCandleChart.js
import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';

export const IndicesCandleChart = ({ data, theme }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const seriesRef = useRef();

  // 1. Initialize Chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: theme.cardBg },
        textColor: theme.textSecondary,
      },
      grid: {
        vertLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
        horzLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 400, // Matching the height of the Hero Card
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 10,
        barSpacing: 10,
      },
      crosshair: {
        mode: 1, // Magnet mode
      },
    });

    // CANDLESTICK SERIES
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#4ade80',     // Green for indices
      downColor: '#ef4444',   // Red for indices
      borderVisible: false,
      wickUpColor: '#4ade80',
      wickDownColor: '#ef4444',
    });

    if (data && data.length > 0) {
      series.setData(data);
    }

    chartRef.current = chart;
    seriesRef.current = series;

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, []);

  // 2. Theme Handling
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({
        layout: {
          background: { type: ColorType.Solid, color: theme.cardBg },
          textColor: theme.textSecondary,
        },
        grid: {
          vertLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
          horzLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
        },
      });
    }
  }, [theme]);

  // 3. Real-Time Data Updates (The Tick Aggregator Result)
  useEffect(() => {
    if (!seriesRef.current || data.length === 0) return;

    const currentSeriesData = seriesRef.current.data();
    const lastInternalCandle = currentSeriesData.length > 0 ? currentSeriesData[currentSeriesData.length - 1] : null;
    const lastIncomingCandle = data[data.length - 1];

    if (!lastInternalCandle) {
        seriesRef.current.setData(data);
        return;
    }

    // UPDATE LOGIC:
    // If timestamps match, we are updating the current candle (High/Low/Close changes)
    if (lastIncomingCandle.time === lastInternalCandle.time) {
        seriesRef.current.update(lastIncomingCandle);
    } 
    // If new timestamp, it's a new candle
    else if (lastIncomingCandle.time > lastInternalCandle.time) {
        seriesRef.current.update(lastIncomingCandle);
    } 
    // Fallback for full resets
    else {
        seriesRef.current.setData(data);
    }
  }, [data]);

  // --- CONTROLS ---
  const handleZoomIn = () => chartRef.current?.timeScale().applyOptions({ barSpacing: chartRef.current.timeScale().options().barSpacing * 1.2 });
  const handleZoomOut = () => chartRef.current?.timeScale().applyOptions({ barSpacing: chartRef.current.timeScale().options().barSpacing * 0.8 });
  const handleReset = () => {
      chartRef.current?.timeScale().fitContent();
      chartRef.current?.priceScale('right').applyOptions({ autoScale: true });
  };
  const handleAutoScale = () => {
      chartRef.current?.priceScale('right').applyOptions({ autoScale: true });
      chartRef.current?.timeScale().scrollToRealTime();
  };

  const btnStyle = {
    background: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    border: `1px solid ${theme.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
    color: theme.textSecondary,
    borderRadius: '4px',
    padding: '2px 6px',
    cursor: 'pointer',
    fontSize: '0.7rem',
    backdropFilter: 'blur(4px)',
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />
      
      {/* Control Toolbar */}
      <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 20, display: 'flex', gap: '4px' }}>
        <button onClick={handleZoomIn} style={btnStyle} title="Zoom In">+</button>
        <button onClick={handleZoomOut} style={btnStyle} title="Zoom Out">-</button>
        <button onClick={handleAutoScale} style={btnStyle} title="Auto Scale">Auto</button>
        <button onClick={handleReset} style={btnStyle} title="Reset View">Reset</button>
      </div>
    </div>
  );
};